<template>
  <div class="spl-measure-filter">

    <button
      v-show="!expanded"
      role="button"
      class="spl-measure-filter__text"
      @click="toggleExpanded">
      <p>{{ filterText }}</p>
    </button>
    <div v-show="expanded">
      <div class="spl-measure-filter__row">
        <div>
          <form-label>Country</form-label>
          <country-multiselect
            :selected-keys="countries"
            placeholder="Countries"
            @valuesSelected="onCountriesSelected" />
        </div>
        <div class="spl-measure-filter__date--from">
          <form-label>From</form-label>
          <form-datepicker v-model="dateFrom" />
        </div>
        <div class="spl-measure-filter__date--to">
          <form-label>To</form-label>
          <form-datepicker v-model="dateTo" />
        </div>
      </div>
      <div class="spl-measure-filter__row">
        <div>
          <form-label>Responsible Type</form-label>
          <form-filtered-select
            :selected-key="responsibleType.value"
            :options="responsibleTypeOptions"
            class="spl-create-problem-report__responsible"
            @updateDropdownValue="updateSelections" />
        </div>

        <div>
          <form-label>
            Problem Types
          </form-label>
          <form-filtered-multiselect
            ref="problemTypes"
            :value="problemTypes"
            :disabled="problemTypeOptions.length === 0"
            :options="problemTypeOptions"
            class="spl-create-problem-report__problem-type"
            @input="updateProblemType" />
        </div>

        <div>
          <form-label>
            Providers
          </form-label>
          <form-filtered-multiselect
            ref="problemResponsibles"
            :value="responsibleProviders"
            :disabled="responsibleProvidersOptions.length === 0"
            :options="responsibleProvidersOptions"
            class="spl-create-problem-report__responsible-provider"
            @input="updateProblemResponsible" />
        </div>
      </div>
      <div class="spl-measure-filter__row">
        <div>
          <form-label>Parcel Status</form-label>
          <form-filtered-multiselect
            :value="parcelStatus"
            :options="constants.PARCEL_STATUS_OPTIONS"
            class="spl-create-problem-report__responsible"
            @input="updateParcelStatus" />
        </div>
      </div>
      <div class="spl-measure-filter__row">
        <form-button class="spl-measure-filter__apply" @onClick="handleFilterApply">
          Apply
        </form-button>
        <form-button
          class="spl-measure-filter__cancel"
          :secondary="true"
          @onClick="handleFilterCancel">
          Cancel
        </form-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState, mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import constants from '@/constants'
import { find, map, filter, head, cloneDeep } from 'lodash'
import { ProblemReportFilter } from '../../webApi/DataModels'

export default {
  props: ['value'],
  data () {
    return {
      expanded: false,
      countries: [],
      responsibleType: {},
      problemTypes: [],
      responsibleProviders: [],
      parcelStatus: [],
      updateSelectionCalled: 0,
      dateFormat: 'dd.MM.yyyy',
      dateFrom: moment().startOf('week').subtract(1, 'week').add(1, 'day').toISOString(true),
      dateTo: moment().startOf('week').toISOString(true),
      constants
    }
  },
  computed: {
    ...mapState('problemReport', ['responsibleTypeOptions', 'problemTypeOptions', 'responsibleProvidersOptions', 'measureFilter']),
    ...mapGetters('country', ['countryOptions']),
    ...mapGetters('personalSettings', ['defaultCountries']),
    ...mapGetters('user', ['getCountryPermissions']),
    filterText () {
      var text:string = 'Filter - '
      text += 'Date: ' + moment(this.dateFrom).format('YYYY-MM-DD') + ' - ' + moment(this.dateTo).format('YYYY-MM-DD')
      if (this.countries && this.countries.length > 0) {
        text += ' | Countries: ' + map(filter(this.countryOptions, (countryOption) => {
          return this.countries.indexOf(countryOption.value) !== -1
        }), 'label').join(', ')
      }
      if (this.responsibleType && this.responsibleType.label) {
        text += ' | Responsible Type: ' + this.responsibleType.label
      }
      if (this.problemTypes && this.problemTypes.length > 0) {
        text += ' | Problem Types: ' + map(filter(this.problemTypeOptions, (problemTypeOption) => {
          return this.problemTypes.indexOf(problemTypeOption.value) !== -1
        }), 'label').join(', ')
      }
      if (this.responsibleProviders && this.responsibleProviders.length > 0) {
        text += ' | Providers: ' + map(filter(this.responsibleProvidersOptions, (responsibleProvidersOption) => {
          return this.responsibleProviders.indexOf(responsibleProvidersOption.value) !== -1
        }), 'label').join(', ')
      }
      if (this.responsibleProviders && this.parcelStatus.length > 0) {
        text += ' | Parcel Status: ' + map(filter(constants.PARCEL_STATUS_OPTIONS, (parcelStatusOption) => {
          return this.parcelStatus.indexOf(parcelStatusOption.value) !== -1
        }), 'label').join(', ')
      }
      return text
    }
  },
  watch: {
    dateFrom: function (newVal, oldVal) {
      if (moment(this.dateFrom).unix() > moment(this.dateTo).unix()) {
        this.dateTo = moment(this.dateFrom).toISOString(true)
      }
    },
    dateTo: function (newVal, oldVal) {
      if (moment(this.dateFrom).unix() > moment(this.dateTo).unix()) {
        this.dateFrom = moment(this.dateTo).toISOString(true)
      }
    }
  },
  created () {
    if (this.defaultCountries && this.defaultCountries.length > 0) {
      this.countries = map(this.defaultCountries, (country) => {
        return find(this.countryOptions, { value: country })
      })
    } else if (this.getCountryPermissions.length > 0) {
      this.countries = map(this.getCountryPermissions, (country) => {
        return find(this.countryOptions, { value: country })
      })
    }
    this.countries = map(this.countries, 'value')
    this.responsibleType = find(this.responsibleTypeOptions, { label: 'Courier Partner' })
    this.parcelStatus = map(filter(constants.PARCEL_STATUS_OPTIONS, { label: 'Late' }), 'value')
    this.responsibleProviders = JSON.parse(window.localStorage.getItem('measures_responsible_providers')) || []
    this.problemTypes = JSON.parse(window.localStorage.getItem('measures_responsible_problem_types')) || []
    this.handleFilterApply()
  },
  methods: {
    ...mapActions('problemReport', ['loadProblemTypeList', 'loadResponsibleProviders', 'setMeasureFilter']),
    // When the problem responsible type change, we need to reload the other two selections
    updateSelections (problemResponsibleType) {
      this.updateSelectionCalled++
      this.responsibleType = problemResponsibleType
      if (this.$refs.problemTypes) {
        this.$refs.problemTypes.reset()
      }
      if (this.$refs.problemResponsibles) {
        this.$refs.problemResponsibles.reset()
      }
      this.get_Problem_Type_List(problemResponsibleType)
      this.get_Problem_Responsible_List(problemResponsibleType)
    },
    // ************************ Load the problem types ************************
    get_Problem_Type_List (problemResponsibleType) {
      if (this.updateSelectionCalled > 1) {
        this.problemTypes = []
        window.localStorage.setItem('measures_responsible_problem_types', JSON.stringify([]))
      }
      this.loadProblemTypeList(problemResponsibleType.value)
    },

    // ************************ Load the  responsible list ************************
    get_Problem_Responsible_List (problemResponsibleType) {
      if (this.updateSelectionCalled > 1) {
        this.responsibleProviders = []
        window.localStorage.setItem('measures_responsible_providers', JSON.stringify([]))
      }
      this.loadResponsibleProviders(problemResponsibleType.value)
    },
    updateProblemResponsible (value) {
      this.responsibleProviders = value
      window.localStorage.setItem('measures_responsible_providers', JSON.stringify(value))
    },
    updateProblemType (value) {
      this.problemTypes = value
      window.localStorage.setItem('measures_responsible_problem_types', JSON.stringify(value))
    },
    onCountriesSelected (countries) {
      this.countries = countries
    },
    updateParcelStatus (value) {
      this.parcelStatus = value
    },
    toggleExpanded () {
      this.expanded = !this.expanded
    },
    handleFilterApply () {
      var filter:ProblemReportFilter = {
        Countries: this.countries,
        From_Date: moment(this.dateFrom).toISOString(true),
        To_Date: moment(this.dateTo).toISOString(true),
        Problem_Responsible_Types: [this.responsibleType.value],
        Problem_Types: this.problemTypes,
        Parcel_Status_IDs: this.parcelStatus,
        Responsible_Providers: this.responsibleProviders
      }
      this.setMeasureFilter(cloneDeep(filter))
      this.expanded = false
    },
    handleFilterCancel () {
      this.countries = this.measureFilter.Countries
      this.dateFrom = moment(this.measureFilter.From_Date)
      this.dateTo = moment(this.measureFilter.To_Date)
      if (head(this.measureFilter.Problem_Responsible_Types)) {
        this.responsibleType = head(filter(this.responsibleTypeOptions, { value: head(this.measureFilter.Problem_Responsible_Types) }))
      }
      this.problemTypes = this.measureFilter.Problem_Types
      window.localStorage.setItem('measures_responsible_problem_types', JSON.stringify(this.problemTypes))
      this.responsibleProviders = this.measureFilter.Responsible_Providers
      window.localStorage.setItem('measures_responsible_providers', JSON.stringify(this.responsibleProviders))
      this.expanded = false
    }
  }
}
</script>

<style lang="scss">
  @import '../../variables.scss';

  .spl-measure-filter {
    background-color: $yellowButtonLabelHover;
    padding: 20px $mainPadding 5px $mainPadding;
    z-index: 5;

    &__text {
       width: 100%;
      cursor: pointer;
      background: 0;
      border: 0;
      margin-bottom: 10px;
      text-align: left;
      color: white;
      height: 40px;
      font-family: "Open Sans", sans-serif;
      font-weight: 400;
      font-size: 13px;
      p {
        margin: 0;
      }
      &:focus {
        background-color: grey;
      }
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }

    &__expand {
      color: white;
      width: 400px;
      height: 50px;
      margin-top: -20px;
      cursor: pointer;
    }

    &__row {
      display: flex;
      flex-direction: row;

      & > div {
        width: 32%;
        max-width: 400px;
        margin-right: 10px;
        &:first-child{
          padding-bottom: 20px;
        }
      }

      & .error {
        margin-top: -11px;
      }
    }
    &__apply, &__cancel {
      margin-top: 22px;
      width: 150px;
    }
  }
</style>
